<template>
  <div class="relative w-full" ref="containerRef">
    <!-- Search Input -->
    <div
      class="flex items-center gap-2.5 px-4 py-2 bg-white rounded-xl border transition-colors duration-150"
      :class="[
        isFocused
          ? 'border-[#2A4B72]'
          : 'border-[#E2E8F0] hover:border-[#BDC6D7]'
      ]"
    >
      <Icon
        icon="mdi:magnify"
        class="text-xl shrink-0 text-[#94A3B8]"
      />
      <input
        ref="inputRef"
        v-model="searchQuery"
        :placeholder="t('searchPlaceholder')"
        class="w-full bg-transparent text-[15px] text-[#1E293B] placeholder-[#BDC6D7] outline-none"
        @input="onSearchInput"
        @keydown.enter.prevent="onEnterPress"
        @keydown.down.prevent="moveSelection(1)"
        @keydown.up.prevent="moveSelection(-1)"
        @keydown.escape="closeDropdown"
        @focus="onFocus"
        @blur="handleBlur"
      />
      <div
        v-if="loading"
        class="w-4 h-4 border-2 border-[#2A4B72] border-t-transparent rounded-full animate-spin shrink-0"
      />
    </div>

    <!-- Dropdown - seamless card continuation -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="showDropdown && (results.length > 0 || noResults)"
        class="absolute z-50 w-full mt-1 bg-white rounded-xl border border-[#E2E8F0] overflow-hidden"
      >
        <!-- Results -->
        <div v-if="results.length > 0" ref="resultsListRef" class="max-h-[320px] overflow-y-auto">
          <div
            v-for="(result, index) in results"
            :key="result.id"
            class="flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors duration-75 search-result-item"
            :class="[
              index === selectedIndex
                ? 'bg-[#F1F5F9]'
                : 'hover:bg-[#F8FAFC]'
            ]"
            @mousedown.prevent="navigateToResult(result)"
            @mouseenter="selectedIndex = index"
          >
            <!-- External code -->
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-[15px] text-[#1E293B] shrink-0">{{ result.externalID }}</span>
              <!-- <span v-if="result.displayName && result.displayName !== result.externalID" class="text-sm text-[#64748B] truncate">{{ result.displayName }}</span> -->
            </div>

            <!-- Type badge - outlined pill -->
            <span
              class="text-xs px-3 py-1 rounded-lg border border-[#E2E8F0] text-[#64748B] shrink-0"
            >{{ t(result.entityType) }}</span>
          </div>
        </div>

        <!-- No results -->
        <div v-else-if="noResults" class="px-4 py-5 text-center">
          <div class="text-sm text-[#94A3B8]">{{ t('noResults') }}</div>
        </div>

        <!-- Keyboard hints -->
        <div
          v-if="results.length > 0"
          class="flex items-center justify-center gap-3 px-3 py-1.5 border-t border-[#F1F5F9] bg-[#FAFBFC]"
        >
          <span class="flex items-center gap-1 text-[10px] text-[#94A3B8]">
            <kbd class="px-1 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-mono text-[9px]">↑↓</kbd>
            {{ t('navigate') }}
          </span>
          <span class="flex items-center gap-1 text-[10px] text-[#94A3B8]">
            <kbd class="px-1 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-mono text-[9px]">↵</kbd>
            {{ t('open') }}
          </span>
          <span class="flex items-center gap-1 text-[10px] text-[#94A3B8]">
            <kbd class="px-1 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-mono text-[9px]">esc</kbd>
            {{ t('close') }}
          </span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { Icon } from '@iconify/vue'

interface SearchResultItem {
  id: string
  externalID: string
  entityType: string
  displayName: string
  viewRoute: string
}

interface SearchResponse {
  results: SearchResultItem[]
  searchType: string
}

const router = useRouter()
const http = useHTTP()

const { t } = useI18n({
  messages: {
    en: {
      searchPlaceholder: 'Search by code or scan barcode...',
      noResults: 'No results found',
      navigate: 'navigate',
      open: 'open',
      close: 'close',
      accounts: 'account',
      transactions: 'transaction',
      users: 'employee',
      registers: 'register',
      customers: 'customer',
      vendors: 'vendor',
      items: 'item',
      inventory_adjustments: 'adjustment',
      inventory_receivings: 'receiving',
      inventory_transfers: 'transfer',
      orders: 'order',
      vendor_bills: 'vendor bill',
      examinations: 'examination',
      register_closing: 'reg. closing',
      purchase_orders: 'purchase order',
      insurances: 'insurance',
      deliveries: 'delivery',
      customers_appointments: 'appointment',
      vet_services: 'vet service',
      quotes: 'quote',
      notes: 'note',
      tasks: 'task'
    },
    ar: {
      searchPlaceholder: 'بحث بالرمز أو مسح الباركود...',
      noResults: 'لا توجد نتائج',
      navigate: 'تنقل',
      open: 'فتح',
      close: 'إغلاق',
      accounts: 'حساب',
      transactions: 'عملية',
      users: 'موظف',
      registers: 'صندوق',
      customers: 'زبون',
      vendors: 'مورد',
      items: 'منتج',
      inventory_adjustments: 'تعديل مخزون',
      inventory_receivings: 'استلام مخزون',
      inventory_transfers: 'نقل مخزون',
      orders: 'فاتورة',
      vendor_bills: 'فاتورة مورد',
      examinations: 'فحص',
      register_closing: 'إغلاق صندوق',
      purchase_orders: 'طلب شراء',
      insurances: 'تأمين',
      deliveries: 'توصيل',
      customers_appointments: 'موعد',
      vet_services: 'خدمة بيطرية',
      quotes: 'عرض سعر',
      notes: 'ملاحظة',
      tasks: 'مهمة'
    }
  }
})

const searchQuery = ref('')
const results = ref<SearchResultItem[]>([])
const loading = ref(false)
const showDropdown = ref(false)
const noResults = ref(false)
const selectedIndex = ref(-1)
const isFocused = ref(false)
const inputRef = ref<HTMLInputElement>()
const containerRef = ref<HTMLElement>()
const resultsListRef = ref<HTMLElement>()

let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Scroll selected item into view within the dropdown container
watch(selectedIndex, (index) => {
  if (index < 0) return
  nextTick(() => {
    const list = resultsListRef.value
    if (!list) return
    const items = list.querySelectorAll('.search-result-item')
    const item = items[index] as HTMLElement
    if (!item) return
    const listTop = list.scrollTop
    const listBottom = listTop + list.clientHeight
    const itemTop = item.offsetTop
    const itemBottom = itemTop + item.offsetHeight
    if (itemBottom > listBottom) {
      list.scrollTop = itemBottom - list.clientHeight
    } else if (itemTop < listTop) {
      list.scrollTop = itemTop
    }
  })
})

function onFocus() {
  isFocused.value = true
  if (results.value.length > 0) {
    showDropdown.value = true
  }
}

function onSearchInput() {
  if (debounceTimer) clearTimeout(debounceTimer)
  noResults.value = false
  selectedIndex.value = -1

  const value = searchQuery.value
  if (!value || value.length < 3) {
    results.value = []
    showDropdown.value = false
    loading.value = false
    return
  }

  loading.value = true
  showDropdown.value = true
  debounceTimer = setTimeout(() => performSearch(value), 300)
}

async function performSearch(query: string) {
  try {
    const response = await http.get<SearchResponse>(`/search/global?q=${encodeURIComponent(query)}`)
    results.value = response.results || []
    noResults.value = results.value.length === 0
    showDropdown.value = true

    if (response.searchType === 'barcode' && results.value.length === 1) {
      navigateToResult(results.value[0])
    }
  } catch {
    results.value = []
    noResults.value = true
  } finally {
    loading.value = false
  }
}

function navigateToResult(result: SearchResultItem) {
  searchQuery.value = ''
  results.value = []
  showDropdown.value = false
  noResults.value = false
  selectedIndex.value = -1
  inputRef.value?.blur()
  router.push(result.viewRoute)
}

function onEnterPress() {
  if (selectedIndex.value >= 0 && selectedIndex.value < results.value.length) {
    navigateToResult(results.value[selectedIndex.value])
  } else if (results.value.length === 1) {
    navigateToResult(results.value[0])
  }
}

function moveSelection(direction: number) {
  if (results.value.length === 0) return
  const newIndex = selectedIndex.value + direction
  if (newIndex >= -1 && newIndex < results.value.length) {
    selectedIndex.value = newIndex
  }
}

function closeDropdown() {
  showDropdown.value = false
  selectedIndex.value = -1
}

function handleBlur() {
  isFocused.value = false
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

function searchAndNavigate(query: string) {
  searchQuery.value = query
  performSearch(query)
}

defineExpose({ searchAndNavigate })
</script>
